import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLingui } from '@lingui/react/macro';
import { msg } from '@lingui/macro';
import type { I18n } from '@lingui/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AddressesService } from '@/api/services/AddressesService';
import type { AddressUsageResponse } from '@/types/address';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info } from 'lucide-react';
import { toast } from 'sonner';

const getAddressSchema = (i18n: I18n) => z.object({
  street: z.string().min(1, i18n._(msg`Street is required`)),
  houseNumber: z.string().max(20).optional(),
  info2: z.string().optional(),
  city: z.string().min(1, i18n._(msg`City is required`)),
  zip: z.string().min(1, i18n._(msg`Postal code is required`)),
  countryIso: z.string().min(2, i18n._(msg`Country is required`)).max(2),
});

type AddressFormData = z.infer<ReturnType<typeof getAddressSchema>>;

interface AddressEditDialogProps {
  addressId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddressUpdated: (newAddressId?: string) => void;
}

const COUNTRIES = [
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'PT', name: 'Portugal' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'AT', name: 'Austria' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'IE', name: 'Ireland' },
];

export function AddressEditDialog({
  addressId,
  open,
  onOpenChange,
  onAddressUpdated,
}: AddressEditDialogProps) {
  const { t, i18n } = useLingui();
  const queryClient = useQueryClient();

  const addressSchema = useMemo(() => getAddressSchema(i18n), [i18n]);

  const { data: address, isLoading: isLoadingAddress } = useQuery({
    queryKey: ['address', addressId],
    queryFn: () => AddressesService.getApiV1Addresses1(addressId),
    enabled: open && !!addressId,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });

  const { data: usage, isLoading: isLoadingUsage } = useQuery<AddressUsageResponse>({
    queryKey: ['address-usage', addressId],
    queryFn: () => AddressesService.getApiV1AddressesUsage(addressId),
    enabled: open && !!addressId,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });

  const form = useForm<AddressFormData>({
    resolver: zodResolver(addressSchema),
    defaultValues: {
      street: '',
      houseNumber: '',
      info2: '',
      city: '',
      zip: '',
      countryIso: 'CH',
    },
  });

  useEffect(() => {
    if (address) {
      form.reset({
        street: address.street || '',
        houseNumber: address.houseNumber || '',
        info2: address.info2 || '',
        city: address.city || '',
        zip: address.zip || '',
        countryIso: address.countryIso || 'CH',
      });
    }
  }, [address, form]);

  // Copy-on-write: always POST a new address. The backend dedups against an
  // existing canonical row if one exists, so re-entering the same values never
  // creates duplicates. The parent entity then repoints its address_id FK to
  // the returned id. Other entities that still reference the old address are
  // unaffected.
  const saveMutation = useMutation({
    mutationFn: (data: AddressFormData) => {
      return AddressesService.postApiV1Addresses({
        street: data.street,
        houseNumber: data.houseNumber || undefined,
        info2: data.info2 || undefined,
        city: data.city,
        zip: data.zip,
        countryIso: data.countryIso,
      });
    },
    onSuccess: (savedAddress) => {
      queryClient.invalidateQueries({ queryKey: ['addresses'] });
      queryClient.invalidateQueries({ queryKey: ['address-usage'] });
      toast.success(t`Address saved`);
      onAddressUpdated(savedAddress.id);
      onOpenChange(false);
    },
    onError: () => {
      toast.error(t`Failed to save address`);
    },
  });

  const onSubmit = (values: AddressFormData) => {
    saveMutation.mutate(values);
  };

  const isLoading = isLoadingAddress || isLoadingUsage;
  const sharedWithOthers = (usage?.totalUsage ?? 0) > 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>{t`address.form.title.edit`}</DialogTitle>
          <DialogDescription>
            {t`Update the address details below`}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-4">
            <div className="h-10 bg-muted animate-pulse rounded" />
            <div className="h-10 bg-muted animate-pulse rounded" />
            <div className="h-10 bg-muted animate-pulse rounded" />
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {sharedWithOthers && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    {i18n._(
                      {
                        id: "address.form.sharedWarning",
                        message:
                          "{count, plural, one {This address is also used by # other record. Saving updates only this record; the other keeps the original address.} other {This address is also used by # other records. Saving updates only this record; the others keep the original address.}}",
                      },
                      { count: (usage?.totalUsage ?? 1) - 1 }
                    )}
                  </AlertDescription>
                </Alert>
              )}

              <div className="grid grid-cols-4 gap-4">
                <FormField
                  control={form.control}
                  name="street"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <FormLabel>{t`address.form.fields.street`}</FormLabel>
                      <FormControl>
                        <Input placeholder={t`address.form.placeholder.streetAddress`} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="houseNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t`address.form.fields.houseNumber`}</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="info2"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t`address.form.label.addressLine2`}</FormLabel>
                    <FormControl>
                      <Input placeholder={t`Suite 100`} {...field} />
                    </FormControl>
                    <FormDescription>
                      {t`Apartment, suite, unit, building, floor, etc. (optional)`}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="zip"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t`address.form.label.postalCode`}</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t`City`}</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="countryIso"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t`Country`}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t`Select a country`} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {COUNTRIES.map((country) => (
                          <SelectItem key={country.code} value={country.code}>
                            {country.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  {t({ id: 'common.cancel', message: 'Cancel' })}
                </Button>
                <Button type="submit" disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? t`Saving...` : t`address.form.title.update`}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
